<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, onMounted, ref } from 'vue'
import { api } from '@/lib/http.js'
import { ENTITIES } from '@/lib/entities.js'
import { useMeta } from '@/stores/meta.js'
import { reportError } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import UiButton from '@/components/ui/UiButton.vue'

/**
 * Who comes to an event (D-139): contacts and leads, one by one or a whole marketing list, each
 * with how they came — invited, registered, attended… — changed here.
 */
const props = defineProps({ record: { type: Object, required: true }, canEdit: Boolean })
const emit = defineEmits(['changed'])
const meta = useMeta()
const rows = ref([])
const kind = ref('contact')
const pick = ref(null)
const fromList = ref(null)
const statuses = computed(() => meta.dictionary('event_participant_status').entries)

async function load() {
  try {
    rows.value = (await api(`/events/${props.record.uuid}/participants`)).data
  } catch (e) {
    reportError(e)
  }
}
onMounted(load)
async function add(body) {
  try {
    await api(`/events/${props.record.uuid}/participants`, { method: 'POST', body })
    pick.value = null
    fromList.value = null
    await load()
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
async function setStatus(row, status) {
  try {
    await api(`/event-participants/${row.uuid}`, { method: 'PATCH', body: { status } })
    row.status = status
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
async function remove(row) {
  try {
    await api(`/event-participants/${row.uuid}`, { method: 'DELETE' })
    await load()
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
const count = (status) => rows.value.filter((r) => r.status === status).length
</script>

<template>
  <section class="card" data-testid="event-participants">
    <header class="flex flex-wrap items-center gap-2 border-b border-default px-3 py-2">
      <h3 class="flex-1 text-sm font-semibold">{{ $t('marketing.participants') }} <span class="font-normal text-faint">({{ rows.length }}<template v-if="record.capacity"> / {{ record.capacity }}</template>)</span></h3>
      <span v-for="s in statuses" :key="s.key" class="text-xs text-muted">{{ meta.localized(s.label) }}: <b class="tabular-nums">{{ count(s.key) }}</b></span>
    </header>
    <div v-if="canEdit" class="grid gap-2 border-b border-default p-3 sm:grid-cols-[8rem_1fr_auto]">
      <select v-model="kind" class="input"><option value="contact">{{ $t('entities.contact.one') }}</option><option value="lead">{{ $t('entities.lead.one') }}</option></select>
      <RefPicker :key="kind" v-model="pick" :entity="kind" data-testid="participant-picker" />
      <UiButton variant="primary" icon="fa-plus" :disabled="!pick" data-testid="participant-add" @click="add({ entity: kind, uuids: [pick] })">{{ $t('common.add') }}</UiButton>
      <span class="self-center text-xs text-muted">{{ $t('marketing.orWholeList') }}</span>
      <RefPicker v-model="fromList" entity="marketing_list" :allow-create="false" />
      <UiButton icon="fa-users" :disabled="!fromList" @click="add({ marketing_list_uuid: fromList })">{{ $t('marketing.inviteList') }}</UiButton>
    </div>
    <p v-if="!rows.length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="row in rows" :key="row.uuid" class="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" :data-participant="row.participant.label">
        <i :class="['fa-solid fa-fw text-xs', ENTITIES[row.participant.entity].icon, ENTITIES[row.participant.entity].color]" aria-hidden="true"></i>
        <RecordLink class="link min-w-32 flex-1 truncate" :entity="row.participant.entity" :uuid="row.participant.uuid">{{ row.participant.label }}</RecordLink>
        <span class="min-w-0 max-w-[45%] shrink truncate text-xs text-muted">{{ row.participant.email || '' }}</span>
        <select class="input w-36 py-1 text-sm" :value="row.status" :disabled="!canEdit" :aria-label="$t('common.status')" @change="setStatus(row, $event.target.value)">
          <option v-for="s in statuses" :key="s.key" :value="s.key">{{ meta.localized(s.label) }}</option>
        </select>
        <UiButton v-if="canEdit" size="xs" variant="ghost" icon="fa-xmark" class="hover:text-red-600" :aria-label="$t('common.remove')" @click="remove(row)" />
      </li>
    </ul>
  </section>
</template>
